import { useEffect, useState } from "react";
import { Gift, Copy, Check, ArrowRight } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/** Refer-a-friend card for the client dashboard.
 *  Shows the user's unique referral link + clear "1 point per converted project" reward
 *  and conversion stats once they have any. Stays tiny and out-of-the-way until expanded. */
export function ReferralCard() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/referrals/me")
      .then(({ data }) => {
        if (!cancelled) setData(data);
      })
      .catch(() => {
        /* silent — non-critical */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!data) return null;

  const origin = process.env.REACT_APP_PUBLIC_BASE_URL || window.location.origin;
  const shareUrl = `${origin.replace(/\/$/, "")}/register?ref=${data.code}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Referral link copied");
      setTimeout(() => setCopied(false), 1800);
    } catch (_) {
      window.prompt("Copy your referral link:", shareUrl);
    }
  };

  return (
    <div
      className="border border-[#262626] bg-[#0a0a0a] mb-6 transition-colors hover:border-[#FF2D2D]/40"
      data-testid="referral-card"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 p-4 text-left"
        data-testid="referral-toggle"
      >
        <div className="w-9 h-9 border border-[#FF2D2D] bg-[#FF2D2D]/10 flex items-center justify-center shrink-0">
          <Gift size={18} className="text-[#FF2D2D]" weight="fill" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF2D2D]">Refer a friend</div>
          <div className="text-sm mt-0.5">
            Earn <span className="font-semibold text-white">0.5 points</span> for every project they pay for.
          </div>
        </div>
        {data.points_earned > 0 && (
          <div className="text-right shrink-0">
            <div className="font-display text-xl tabular-nums">{data.points_earned}</div>
            <div className="text-[10px] uppercase tracking-wider text-[#a3a3a3]">earned</div>
          </div>
        )}
        <ArrowRight
          size={14}
          className={`text-[#a3a3a3] shrink-0 transition-transform ${open ? "rotate-90" : ""}`}
        />
      </button>

      {open && (
        <div className="border-t border-[#262626] p-5 space-y-5" data-testid="referral-details">
          {/* How it works */}
          <div className="text-xs text-[#a3a3a3] leading-relaxed">
            Share your link below. When someone signs up through it and pays for a project, you
            automatically get <span className="text-white font-semibold">0.5 points</span> credited
            to your account — no caps, no waiting period. Use points toward your own future projects.
          </div>

          {/* Share link */}
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#a3a3a3] mb-2">Your link</div>
            <div className="flex gap-2">
              <input
                readOnly
                value={shareUrl}
                onFocus={(e) => e.target.select()}
                className="flex-1 bg-[#050505] border border-[#262626] px-3 py-2 text-xs text-[#fafafa] font-mono outline-none focus:border-[#FF2D2D]"
                data-testid="referral-link-input"
              />
              <button
                onClick={copy}
                data-testid="referral-copy-btn"
                className={`px-3 py-2 text-xs font-bold uppercase tracking-wider border flex items-center gap-1.5 transition-colors ${
                  copied
                    ? "border-[#FF2D2D] bg-[#FF2D2D] text-white"
                    : "border-[#262626] hover:border-[#FF2D2D] hover:text-[#FF2D2D]"
                }`}
              >
                {copied ? <Check size={12} weight="bold" /> : <Copy size={12} />}
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
            <div className="text-[10px] text-[#666] mt-1.5 font-mono">Code: {data.code}</div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-px bg-[#262626] border border-[#262626]">
            <div className="bg-[#050505] p-3" data-testid="referral-stat-signups">
              <div className="font-display text-2xl tabular-nums">{data.total_referred}</div>
              <div className="text-[10px] uppercase tracking-wider text-[#a3a3a3] mt-1">Sign-ups</div>
            </div>
            <div className="bg-[#050505] p-3" data-testid="referral-stat-converted">
              <div className="font-display text-2xl tabular-nums">{data.total_converted_clients}</div>
              <div className="text-[10px] uppercase tracking-wider text-[#a3a3a3] mt-1">Converted</div>
            </div>
            <div className="bg-[#050505] p-3" data-testid="referral-stat-points">
              <div className="font-display text-2xl tabular-nums text-[#FF2D2D]">
                {data.points_earned}
              </div>
              <div className="text-[10px] uppercase tracking-wider text-[#a3a3a3] mt-1">Points</div>
            </div>
          </div>

          {/* Recent credits */}
          {data.recent_credits?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#a3a3a3] mb-2">
                Recent rewards
              </div>
              <div className="divide-y divide-[#262626] border border-[#262626]">
                {data.recent_credits.slice(0, 5).map((c) => (
                  <div
                    key={c.id}
                    className="px-3 py-2 flex items-center justify-between text-xs"
                    data-testid={`referral-credit-${c.id}`}
                  >
                    <div className="text-[#a3a3a3] truncate">
                      <span className="text-white">{c.referred_user_name || c.referred_user_email}</span>{" "}
                      paid a project
                    </div>
                    <div className="text-[#FF2D2D] font-semibold tabular-nums shrink-0 ml-2">
                      +{c.points} pt{c.points === 1 ? "" : "s"}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
